import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MarketPageService } from '../services/market-page.service';
import { Administrator, Farm, Organisation } from '../../models/types';
import { FormsModule } from '@angular/forms';
import { FarmListComponent } from '../components/farm-list/farm-list.component';
import { ProfilePageService } from '../services/profile-page.service';
import { AllUsersComponent } from "../components/all-users/all-users.component";
import { EventService } from '../services/event.service';
import { Event } from '../../models/types';
import { EventListComponent } from '../components/event-list/event-list.component';
import { LoginService } from '../services/login.service';
import { OrganisationServiceService } from '../services/organisation-service.service';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

declare var bootstrap: any;
@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FarmListComponent,
    AllUsersComponent, 
    EventListComponent,
    ReactiveFormsModule
    ],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss'
})
export class DashboardPageComponent {

  allFarms: Farm[] = [];
  farmCount: number = 0;
  message = ''
  isOnMarketPage = false;
  allEvents: Event[] = [];
  organisations: Organisation[] = [];
  isEditMode: boolean = false;
  editingOrganisationId: number | null = null;
  @ViewChild('orgFormSection') orgFormSection!: ElementRef;
  organisationForm: FormGroup;

  admins: Administrator[] = [];
  newAdmin: Administrator = {
    username: '',
    contact_email: '',
    role: '',
    organisationID: 0,
    password: ''
  };
  constructor(
    private router: Router,
    private marketPageService: MarketPageService,
    private profilePageService: ProfilePageService,
    private eventService: EventService,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef,
    private organisationService: OrganisationServiceService,
    private fb: FormBuilder) {
      this.organisationForm = this.fb.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: ['', Validators.required],
        address: ['', Validators.required],
        type: ['', Validators.required],
        postalCode: ['', Validators.required],
        taxNumber: ['', Validators.required],
        IBAN: ['', Validators.required],
        VATNumber: ['', Validators.required],
      });
    }

  ngOnInit() {
    this.fetchAllFarms();
    this.getAllEvents();
    this.loadOrganisations();
  }

  fetchAllFarms() {
    this.marketPageService.getAllFarms().subscribe((response) => {
      this.allFarms = response.body!.farms;
      this.farmCount = response.body!.count;
      console.log('Farms fetched:', this.allFarms);
    });
  }

  deleteFarm(farmId: number) {
    this.marketPageService.deleteFarm(farmId).subscribe((response) => {
      console.log('Farm deleted:', response.body);
      this.fetchAllFarms();
    });
  }

  getAllEvents() {
    this.eventService.getAllEvents().subscribe((response) => {
      this.allEvents = response.events!;
      console.log('Events fetched:', this.allEvents);
    });
  }

  loadOrganisations() {
    this.loginService.getAllOrganisations().subscribe({
      next: (response) => {
        if (response.body && Array.isArray(response.body.organisations)) {
          this.organisations = response.body.organisations;
  
          // Fetch administrators
          this.organisationService.getAdministrators().subscribe((adminResponse) => {
            if (adminResponse && Array.isArray(adminResponse.administrators)) {
              const allAdmins = adminResponse.administrators;
  
              // Map administrators to their corresponding organisation
              this.organisations.forEach((org: Organisation) => {
                org.administrators = allAdmins.filter(
                  (admin: Administrator) => admin.organisationID === org.organisationID
                );
              });
  
              console.log('Organisations with admins:', this.organisations);
              this.cdr.detectChanges();
            } else {
              console.warn('No administrators returned or unexpected structure:', adminResponse);
            }
          });
  
        } else {
          console.error('Unexpected response structure:', response.body);
        }
      },
      error: (err) => {
        console.error('Error fetching organisations:', err);
      }
    });
  }
  
  

  deleteOrganisation(organisationId: number) {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this organisation?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.organisationService.deleteOrganisation(organisationId).subscribe((response) => {
          console.log('Organisation deleted:', response.body);
          Swal.fire({
            icon: 'success',
            title: 'Organisation deleted successfully',
            showConfirmButton: false,
            timer: 1500
          });
          this.loadOrganisations();
          //window.location.reload(); // Reload the page to reflect changes
        });
      }
    });
  }
  

  editOrganisation(organisation: Organisation) {
    this.isEditMode = true;
    this.editingOrganisationId = organisation.organisationID;

    const collapseEl = document.getElementById('orgFormCollapse');
    const bsCollapse = new bootstrap.Collapse(collapseEl!, {
      toggle: false
    });
    bsCollapse.show();

    // Smooth scroll to the form
    setTimeout(() => {
      this.orgFormSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  
    this.organisationForm.patchValue({
      name: organisation.name,
      description: organisation.description,
      email: organisation.email,
      phone: organisation.phone,
      address: organisation.address,
      type: organisation.type,
      postalCode: organisation.postalCode,
      taxNumber: organisation.taxNumber,
      IBAN: organisation.IBAN,
      VATNumber: organisation.VATNumber
    });
  }

  submitOrganisation() {
    if (this.organisationForm.invalid) {
      this.organisationForm.markAllAsTouched();
      return;
    }
  
    const organisationData = this.organisationForm.value;
  
    if (this.isEditMode && this.editingOrganisationId) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'This will update the organisation details.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, update it!',
      }).then((result) => {
        if (result.isConfirmed) {
          // Update
          // Add the organisation ID to the data
          organisationData.id = this.editingOrganisationId;
  
          this.organisationService.updateOrganisation(organisationData).subscribe({
            next: () => {
              Swal.fire('Updated!', 'Organisation updated successfully.', 'success');
              this.resetForm();
              this.loadOrganisations();
            },
            error: (err) => {
              console.error('Update failed', err);
              Swal.fire('Error', 'Failed to update organisation', 'error');
            }
          });
        }
      });
    } else {
      // Add
      this.organisationService.addOrganisation(organisationData).subscribe({
        next: () => {
          Swal.fire('Added!', 'Organisation added successfully.', 'success');
          this.resetForm();
          this.loadOrganisations();
        },
        error: (err) => {
          console.error('Add failed', err);
          Swal.fire('Error', 'Failed to add organisation', 'error');
        }
      });
    }
  }
  
  resetForm() {
    this.organisationForm.reset();
    this.isEditMode = false;
    this.editingOrganisationId = null;
  }
  
  deleteAdmin(adminId: number) {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently remove the administrator.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete'
    }).then((result) => {
      if (result.isConfirmed) {
        this.organisationService.deleteAdministrator(adminId).subscribe({
          next: () => {
            Swal.fire('Deleted!', 'Administrator has been removed.', 'success');
            this.loadOrganisations();
          },
          error: (err) => {
            Swal.fire('Error', 'Failed to delete administrator', 'error');
            console.error(err);
          }
        });
      }
    });
  }  

  addAdmin(orgID: number) {
    if (!this.newAdmin.username || !this.newAdmin.password || !this.newAdmin.contact_email || !this.newAdmin.role) {
      Swal.fire('Error', 'Please fill all fields', 'error');
      return;
    }
    this.newAdmin.organisationID = orgID; // Set the organisation ID for the new admin
  
    this.organisationService.addAdministrator(this.newAdmin).subscribe({
      next: () => {
        Swal.fire('Success', 'Administrator added', 'success');
        this.newAdmin = { username: '', contact_email: '', role: '', organisationID: 0, password: '' };
        this.loadOrganisations();
      },
      error: (err) => {
        Swal.fire('Error', 'Failed to add administrator', 'error');
        console.error(err);
      }
    });
  }


  
}
